import type {
    BetaCreatePageInput,
    BetaCreatePageKind,
    BetaCreatePageSelection,
    BetaPageCombination,
    BetaPageCombinationGroups,
} from '@/types/beta-dashboard'

export interface BetaCreatePageLayoutStep {
    id: 'page' | 'listing' | 'content'
    title: string
    description: string
    combinations: BetaPageCombination[]
}

const combinationsFor = (
    groups: BetaPageCombinationGroups,
    component: string,
    predicate?: (combination: BetaPageCombination) => boolean,
) => {
    const combinations = Array.isArray(groups[component]) ? groups[component] : []
    return predicate ? combinations.filter(predicate) : combinations
}

const isEditorialContent = (combination: BetaPageCombination) => (
    Boolean(combination.hasComments || combination.hasSidebar)
)

const componentByKind: Partial<Record<BetaCreatePageKind, string>> = {
    about: 'about',
    contact: 'contact',
    testimonials: 'comments',
    newsletter: 'newsletter',
    store: 'storelist',
}

const endpointTypeByKind: Partial<Record<BetaCreatePageKind, string>> = {
    link: 'link',
    about: 'about',
    contact: 'contact',
    testimonials: 'comments',
    newsletter: 'newsletter',
    store: 'storelist',
}

export const betaCreatePageLayoutSteps = (
    kind: BetaCreatePageKind,
    groups: BetaPageCombinationGroups,
): BetaCreatePageLayoutStep[] => {
    if (kind === 'text' || kind === 'link') return []

    if (kind === 'blog' || kind === 'portfolio') {
        const contentPredicate = kind === 'blog'
            ? isEditorialContent
            : (combination: BetaPageCombination) => !isEditorialContent(combination)

        return [
            {
                id: 'listing',
                title: kind === 'blog'
                    ? 'Escolha o layout da listagem do blog'
                    : 'Escolha o layout da listagem',
                description: 'Este modelo organiza os posts ou projetos na página principal.',
                combinations: combinationsFor(groups, 'blog'),
            },
            {
                id: 'content',
                title: kind === 'blog'
                    ? 'Escolha o layout interno dos posts'
                    : 'Escolha o layout das fotos',
                description: 'Este modelo será usado quando um visitante abrir cada conteúdo.',
                combinations: combinationsFor(groups, 'maincontent', contentPredicate),
            },
        ]
    }

    const component = componentByKind[kind] ?? 'maincontent'

    const titles: Record<Exclude<BetaCreatePageKind, 'blog' | 'portfolio' | 'link'>, string> = {
        text: 'Escolha o layout da página',
        gallery: 'Escolha o layout da galeria',
        about: 'Escolha o layout da página Sobre',
        contact: 'Escolha o layout do formulário',
        testimonials: 'Escolha o layout dos depoimentos',
        newsletter: 'Escolha o layout da newsletter',
        store: 'Escolha o layout da loja',
    }

    return [{
        id: 'page',
        title: titles[kind],
        description: 'Selecione uma prévia para definir a estrutura inicial da página.',
        combinations: combinationsFor(groups, component),
    }]
}

export const betaCreatePageAutomaticCombinations = (
    kind: BetaCreatePageKind,
    groups: BetaPageCombinationGroups,
): BetaPageCombination[] => {
    if (kind !== 'text') return []

    const defaults = combinationsFor(groups, 'default')
    const singlePage = defaults.find((combination) => (
        combination.id === 'unic-page' || combination.component === 'maincontent'
    ))

    return singlePage ? [singlePage] : []
}

export const betaCreatePageKindUnavailable = (
    kind: BetaCreatePageKind,
    groups: BetaPageCombinationGroups,
) => kind === 'text'
    ? !betaCreatePageAutomaticCombinations(kind, groups).length
    : betaCreatePageLayoutSteps(kind, groups).some((step) => !step.combinations.length)

export const buildBetaCreatePageInput = (
    selection: BetaCreatePageSelection,
): BetaCreatePageInput | null => {
    const expectedCombinations = selection.kind === 'link'
        ? 0
        : selection.kind === 'blog' || selection.kind === 'portfolio'
            ? 2
            : 1

    if (selection.combinations.length !== expectedCombinations) return null

    const type = endpointTypeByKind[selection.kind]
        ?? (selection.kind === 'blog' || selection.kind === 'portfolio'
            ? 'blog'
            : 'maincontent')

    const options = selection.kind === 'text'
        ? { frames: ['text'] }
        : selection.kind === 'gallery'
            ? { frames: ['gallery'] }
            : selection.kind === 'portfolio'
                ? { galleryType: 'isotope' }
                : {}

    return {
        name: selection.name.trim(),
        target: 0,
        type,
        options,
        combination: selection.combinations,
    }
}
